import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Loader2, BarChart3, Users, CalendarDays, Building2 } from "lucide-react";
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";

const COLORS = ["#EA580C", "#84CC16", "#3B82F6", "#8B5CF6", "#F59E0B", "#EF4444"];
const STATUS_COLORS = { CONFIRMED: "#22C55E", COMPLETED: "#22C55E", PENDING_CASH: "#F59E0B", PENDING_PAYMENT: "#F59E0B", CANCELLED: "#EF4444" };

export default function CondoReports() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/condo/reports").then(r => { setData(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-court-orange" /></div>;
  if (!data) return <div className="text-center py-20 font-heading text-xl">Unable to load reports</div>;

  return (
    <div className="max-w-5xl mx-auto px-4 md:px-8 py-6 md:py-10" data-testid="condo-reports-page">
      <h1 className="font-heading text-3xl md:text-4xl font-bold uppercase tracking-tight flex items-center gap-3 mb-1">
        <BarChart3 className="w-8 h-8 text-court-orange" />Reports
      </h1>
      <p className="text-muted-foreground mb-8">Booking activity and facility usage</p>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <div className="bg-white border-2 border-slate-100 p-5" data-testid="total-bookings-card">
          <CalendarDays className="w-5 h-5 text-court-orange mb-2" />
          <p className="font-heading text-3xl font-bold">{data.total_bookings}</p>
          <p className="text-[10px] font-heading uppercase tracking-[0.15em] text-muted-foreground">Total Bookings</p>
        </div>
        <div className="bg-white border-2 border-slate-100 p-5" data-testid="resident-bookings-card">
          <Users className="w-5 h-5 text-lime-volt mb-2" />
          <p className="font-heading text-3xl font-bold">{data.resident_bookings}</p>
          <p className="text-[10px] font-heading uppercase tracking-[0.15em] text-muted-foreground">By Residents</p>
        </div>
        <div className="bg-white border-2 border-slate-100 p-5" data-testid="non-resident-bookings-card">
          <Users className="w-5 h-5 text-blue-500 mb-2" />
          <p className="font-heading text-3xl font-bold">{data.non_resident_bookings}</p>
          <p className="text-[10px] font-heading uppercase tracking-[0.15em] text-muted-foreground">Non-Residents</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        {/* Bookings by Status */}
        <div className="bg-white border-2 border-slate-100 p-6" data-testid="status-chart">
          <h3 className="font-heading text-sm uppercase tracking-[0.15em] text-muted-foreground mb-4">By Status</h3>
          {data.bookings_by_status?.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">No data</p>
          ) : (
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={data.bookings_by_status} dataKey="count" nameKey="status" cx="50%" cy="50%" outerRadius={70}
                    label={({ status, count }) => `${status.replace("_"," ")} (${count})`} labelLine={false}>
                    {data.bookings_by_status.map((e, i) => <Cell key={i} fill={STATUS_COLORS[e.status] || COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        {/* Bookings by Venue */}
        <div className="bg-white border-2 border-slate-100 p-6" data-testid="venue-chart">
          <h3 className="font-heading text-sm uppercase tracking-[0.15em] text-muted-foreground mb-4">By Venue</h3>
          {data.bookings_by_venue?.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">No data</p>
          ) : (
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.bookings_by_venue} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis type="number" tick={{ fontSize: 10 }} />
                  <YAxis dataKey="venue" type="category" tick={{ fontSize: 10 }} width={120} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#EA580C" radius={[0, 2, 2, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>

      {/* Top Bookers */}
      <div className="bg-white border-2 border-slate-100 p-6" data-testid="top-bookers">
        <h3 className="font-heading text-sm uppercase tracking-[0.15em] text-muted-foreground mb-4 flex items-center gap-2">
          <Users className="w-4 h-4" />Top Bookers
        </h3>
        {data.top_bookers?.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-8">No booking data yet</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {data.top_bookers?.map((p, i) => (
              <div key={i} className="flex items-center gap-3 p-3 bg-slate-50 text-sm">
                <span className="font-heading text-xl font-bold text-court-orange w-8 text-center">{i + 1}</span>
                <div className="flex-1"><p className="font-medium">{p.name}</p></div>
                <span className="font-heading font-bold">{p.bookings} bookings</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
